import { createReducer, combineReducers } from '@reduxjs/toolkit';

import { Nullable, RequestState } from 'modules/types/common';

import { MedicalHistoryData, ShareProcedures } from 'store/medicalHistory/types';
import { switchAccountSuccess } from 'store/account/actions';
import * as actions from './actions';

export interface ProceduresListState {
  loading: boolean;
  error: null | Error;
  data: MedicalHistoryData[];
  lastUpdated: string | undefined;
  requestState: RequestState;
}

export interface ShareProceduresState {
  isSendingProcedures: boolean;
  sharedProceduresData: ShareProcedures | {};
  sharedProceduresError: Nullable<Error>;
}

export interface ProceduresDownloadsState {
  isDownloading: boolean;
  error: Nullable<Error>;
}

export interface ProceduresState {
  procedures: ProceduresListState;
  shareProcedures: ShareProceduresState;
  downloadProcedures: ProceduresDownloadsState;
}

export const proceduresInitialState = (): ProceduresListState => ({
  loading: false,
  data: [],
  error: null,
  lastUpdated: undefined,
  requestState: RequestState.INITIALIZED
});

const proceduresReducer = createReducer(proceduresInitialState(), {
  [actions.FETCH_PROCEDURES]: state => {
    state.loading = true;
    state.error = null;
    state.requestState = RequestState.REQUESTING;
  },
  [actions.FETCH_PROCEDURES_FAIL]: (state, action) => {
    state.loading = false;
    state.error = action.error;
    state.requestState = RequestState.ERROR;
  },
  [actions.FETCH_PROCEDURES_SUCCESS]: (state, action) => {
    state.loading = false;
    state.data = action.payload.data;
    state.requestState = RequestState.SUCCESS;
  },
  [actions.FETCH_PROCEDURES_CACHE_SUCCESS]: (state, action) => {
    state.data = action.payload.data;
    state.lastUpdated = action.payload.cacheUpdatedAt;
  },
  [switchAccountSuccess.type]: () => proceduresInitialState()
});

export const shareProceduresInitialState = (): ShareProceduresState => ({
  isSendingProcedures: false,
  sharedProceduresData: {},
  sharedProceduresError: null
});

const shareProceduresReducer = createReducer(shareProceduresInitialState(), {
  [actions.SHARE_PROCEDURES]: state => {
    state.isSendingProcedures = true;
    state.sharedProceduresError = null;
  },
  [actions.SHARE_PROCEDURES_SUCCESS]: (state, action) => {
    state.isSendingProcedures = false;
    state.sharedProceduresData = action.payload.data;
  },
  [actions.SHARE_PROCEDURES_FAIL]: (state, action) => {
    state.isSendingProcedures = false;
    state.sharedProceduresError = action.error;
  }
});

export const proceduresDownloadsInitialState = (): ProceduresDownloadsState => ({
  isDownloading: false,
  error: null
});

const proceduresDownloadsReducer = createReducer(proceduresDownloadsInitialState(), {
  [actions.DOWNLOAD_PROCEDURES]: state => {
    state.isDownloading = true;
    state.error = null;
  },
  [actions.DOWNLOAD_PROCEDURES_SUCCESS]: state => {
    state.isDownloading = false;
  },
  [actions.DOWNLOAD_PROCEDURES_FAIL]: (state, action) => {
    state.isDownloading = false;
    state.error = action.error;
  }
});

export default combineReducers({
  procedures: proceduresReducer,
  shareProcedures: shareProceduresReducer,
  downloadProcedures: proceduresDownloadsReducer
});
